// src/web/client/src/utils/api.js
// Wrapper בטוח לכל קריאות ה-API - עם טיפול במנויים

import { storage } from './storage';
import { getShopifySessionToken } from './shopifyAuth';
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'https://api.ecomoria.com/api';
// ============================================
// XHR-BASED FETCH (bypasses Shopify's fetch override)
// ============================================

const xhrFetch = (url, options = {}) => {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    const method = options.method || 'GET';

    xhr.open(method, url);

    // Bound every request so a stuck/dead backend can't hang the UI forever.
    // Without this, components stay in `loading: true` indefinitely and the
    // nav appears frozen even though React Router itself is fine.
    xhr.timeout = options.timeout || 20000;

    // Set headers
    const headers = options.headers || {};
    Object.keys(headers).forEach(key => {
      xhr.setRequestHeader(key, headers[key]);
    });

    xhr.onload = () => {
      const response = {
        ok: xhr.status >= 200 && xhr.status < 300,
        status: xhr.status,
        statusText: xhr.statusText,
        headers: {
          get: (name) => xhr.getResponseHeader(name)
        },
        text: () => Promise.resolve(xhr.responseText),
        json: () => {
          try {
            return Promise.resolve(JSON.parse(xhr.responseText));
          } catch (e) {
            return Promise.reject(new Error('Invalid JSON'));
          }
        }
      };
      resolve(response);
    };
    
    xhr.onerror = () => reject(new Error('Network error'));
    xhr.ontimeout = () => reject(new Error('Request timeout'));
    
    if (options.body) {
      xhr.send(options.body);
    } else {
      xhr.send();
    }
  });
};
// ============================================
// SUBSCRIPTION ERROR HANDLERS
// ============================================

/**
 * טיפול בשגיאת מנוי (402)
 * מנוי פג תוקף או לא קיים
 */
const handleSubscriptionRequired = (responseData, endpoint) => {
  console.warn('[API] Subscription required for:', endpoint);
  
  // שמור את ה-URL הנוכחי לחזרה אחרי תשלום
  const currentPath = window.location.pathname;
  if (currentPath !== '/billing') {
    sessionStorage.setItem('returnAfterBilling', currentPath);
  }
  
  // הפעל event מותאם אישית — SubscriptionGuard מאזין ומציג overlay חוסם
  // בכל מסך. אין יותר window.location.href כאן — זה היה hard reload ששובר
  // את ה-SPA וגורם ל-iframe של Shopify Admin לחזור לדף הראשי במקום להישאר
  // על /billing.
  window.dispatchEvent(new CustomEvent('subscription:required', {
    detail: {
      message: responseData.message || 'Your subscription has expired',
      upgradeUrl: responseData.upgrade_url || '/billing',
      code: responseData.code,
      subscriptionStatus: responseData.subscription_status
    }
  }));

  return null;
};

/**
 * טיפול בשגיאת פיצ'ר לא זמין (403)
 * הפיצ'ר דורש Plan גבוה יותר
 */
const handleFeatureNotAvailable = (responseData, endpoint) => {
  console.warn('[API] Feature not available:', responseData.feature, 'for:', endpoint);
  
  // הפעל event מותאם אישית
  window.dispatchEvent(new CustomEvent('subscription:upgrade-needed', { 
    detail: {
      feature: responseData.feature,
      requiredPlan: responseData.required_plan,
      message: responseData.error || 'This feature requires a higher plan',
      upgradeUrl: responseData.upgrade_url || '/billing'
    }
  }));
  
  // החזר אובייקט שגיאה במקום null (כדי שהקומפוננטה תוכל להציג הודעה)
  return {
    error: true,
    code: 'FEATURE_NOT_AVAILABLE',
    feature: responseData.feature,
    requiredPlan: responseData.required_plan,
    message: `This feature requires the ${responseData.required_plan || 'Pro'} plan`
  };
};

// ============================================
// MAIN API WRAPPER
// ============================================

export const safeApi = {
  /**
   * פונקציה בסיסית לקריאות API
   * כולל טיפול אוטומטי בשגיאות מנוי
   */
/*  fetch: async (endpoint, options = {}) => {
    // תמיד קבל shop domain בצורה בטוחה
    const shopDomain = storage.getShopDomain();
    
    // אם אין shop domain תקין, אל תבצע את הקריאה
    if (!shopDomain || shopDomain === 'undefined') {
      console.warn('[API] Skipping call - no valid shop domain');
      return null;
    }
    
    // בנה URL עם shop_domain
    const url = new URL(`${API_BASE_URL}${endpoint}`);
    if (!url.searchParams.has('shop_domain')) {
      url.searchParams.append('shop_domain', shopDomain);
    }
    
    // הכן headers
    const headers = {
      'Content-Type': 'application/json',
      'X-Shop-Domain': shopDomain,
      ...options.headers
    };
    
    const token = localStorage.getItem('token');
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
    
    try {
      const response = await fetch(url.toString(), {
        ...options,
        headers
      });
      
      // --- טיפול בשגיאות מנוי ---
      
      // 402 - מנוי נדרש (פג תוקף או לא קיים)
      if (response.status === 402) {
        const data = await response.json();
        return handleSubscriptionRequired(data, endpoint);
      }
      
      // 403 - פיצ'ר לא זמין (דורש Plan גבוה יותר)
      if (response.status === 403) {
        const data = await response.json();
        if (data.code === 'FEATURE_NOT_AVAILABLE') {
          return handleFeatureNotAvailable(data, endpoint);
        }
        // 403 אחר (לא קשור לפיצ'ר)
        console.error('[API] Forbidden:', endpoint, data);
        return null;
      }
      
      // 401 - לא מחובר
      if (response.status === 401) {
        console.warn('[API] Unauthorized - redirecting to login');
        window.dispatchEvent(new CustomEvent('auth:required'));
        // אופציונלי: הפנה לדף login
        // window.location.href = '/login';
        return null;
      }
      
      // שגיאות אחרות
      if (!response.ok && response.status !== 404) {
        console.error(`[API] Error ${response.status} for ${endpoint}`);
      }
      
      return response;
    } catch (error) {
      console.error('[API] Fetch Error:', error);
      throw error;
    }
  },*/
  fetch: async (endpoint, options = {}) => {
    // תמיד קבל shop domain בצורה בטוחה
    const shopDomain = storage.getShopDomain();
    
    // אם אין shop domain תקין, אל תבצע את הקריאה
    if (!shopDomain || shopDomain === 'undefined') {
      console.warn('[API] Skipping call - no valid shop domain');
      return null;
    }
    
    // בנה URL עם shop_domain
    const url = new URL(`${API_BASE_URL}${endpoint}`);
    if (!url.searchParams.has('shop_domain')) {
      url.searchParams.append('shop_domain', shopDomain);
    }
    
    // הכן headers
    const headers = {
      'Content-Type': 'application/json',
      'X-Shop-Domain': shopDomain,
      ...options.headers
    };
    
    // Try to get Shopify session token first, fallback to localStorage token
    try {
      console.log('[API] Getting session token...');
      const sessionToken = await getShopifySessionToken();
      console.log('[API] Session token:', sessionToken ? 'EXISTS' : 'NULL');
      if (sessionToken) {
        headers['Authorization'] = `Bearer ${sessionToken}`;
        console.log('[API] Authorization header set');
      } else {
        // Fallback for non-embedded context
        const token = localStorage.getItem('token');
        if (token) {
          headers['Authorization'] = `Bearer ${token}`;
        }
      }
    } catch (error) {
      console.warn('[API] Could not get session token, using fallback');
      const token = localStorage.getItem('token');
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }
    }
    
    try {
      //const response = await fetch(url.toString(), {
        const response = await xhrFetch(url.toString(), {
        ...options,
        headers
      });
      
      // 402 - מנוי נדרש
      if (response.status === 402) {
        const data = await response.json();
        return handleSubscriptionRequired(data, endpoint);
      }
      
      // 403 - פיצ'ר לא זמין
      if (response.status === 403) {
        const data = await response.json();
        if (data.code === 'FEATURE_NOT_AVAILABLE') {
          return handleFeatureNotAvailable(data, endpoint);
        }
        console.error('[API] Forbidden:', endpoint, data);
        return null;
      }
      
      // 401 - לא מחובר
      if (response.status === 401) {
        console.warn('[API] Unauthorized - redirecting to login');
        window.dispatchEvent(new CustomEvent('auth:required'));
        return null;
      }
      
      // שגיאות אחרות
      if (!response.ok && response.status !== 404) {
        console.error(`[API] Error ${response.status} for ${endpoint}`);
      }
      
      return response;
    } catch (error) {
      console.error('[API] Fetch Error:', error);
      throw error;
    }
  },

  /**
  /**
   * קריאת GET עם parsing אוטומטי
   */
  get: async (endpoint) => {
    const response = await safeApi.fetch(endpoint);
    if (!response) return null;
    if (response.error) return response; // החזר שגיאת פיצ'ר
    return response.ok ? response.json() : null;
  },

  /**
   * קריאת POST עם parsing אוטומטי
   */
  post: async (endpoint, data) => {
    const response = await safeApi.fetch(endpoint, {
      method: 'POST',
      body: JSON.stringify(data)
    });
    if (!response) return null;
    if (response.error) return response;
    return response.ok ? response.json() : null;
  },

  /**
   * קריאת PUT עם parsing אוטומטי
   */
  put: async (endpoint, data) => {
    const response = await safeApi.fetch(endpoint, {
      method: 'PUT',
      body: JSON.stringify(data)
    });
    if (!response) return null;
    if (response.error) return response;
    return response.ok ? response.json() : null;
  },

  /**
   * קריאת DELETE עם parsing אוטומטי
   */
  delete: async (endpoint) => {
    const response = await safeApi.fetch(endpoint, {
      method: 'DELETE'
    });
    if (!response) return null;
    if (response.error) return response;
    return response.ok ? response.json() : null;
  },

  // ============================================
  // SPECIFIC API MODULES
  // ============================================

  subscription: {
    getInfo: async () => {
      const response = await safeApi.fetch('/subscription/info');
      return response && response.ok ? response.json() : null;
    },
    
    getStatus: async () => {
      const response = await safeApi.fetch('/shopify-billing/status');
      return response && response.ok ? response.json() : null;
    }
  },

  templates: {
    list: async () => {
      const response = await safeApi.fetch('/templates');
      return response && response.ok ? response.json() : [];
    },
    
    get: async (id) => {
      const response = await safeApi.fetch(`/templates/${id}`);
      return response && response.ok ? response.json() : null;
    },
    
    create: async (data) => {
      const response = await safeApi.fetch('/templates', {
        method: 'POST',
        body: JSON.stringify(data)
      });
      return response && response.ok ? response.json() : null;
    },
    
    update: async (id, data) => {
      const response = await safeApi.fetch(`/templates/${id}`, {
        method: 'PUT',
        body: JSON.stringify(data)
      });
      return response && response.ok ? response.json() : null;
    },
    
    delete: async (id) => {
      const response = await safeApi.fetch(`/templates/${id}`, {
        method: 'DELETE'
      });
      return response && response.ok ? response.json() : null;
    }
  },

  messages: {
    list: async () => {
      const response = await safeApi.fetch('/messages?sort=-sent_date');
      return response && response.ok ? response.json() : [];
    },
    
    get: async (id) => {
      const response = await safeApi.fetch(`/messages/${id}`);
      return response && response.ok ? response.json() : null;
    },
    
    send: async (data) => {
      const response = await safeApi.fetch('/send', {
        method: 'POST',
        body: JSON.stringify(data)
      });
      return response && response.ok ? response.json() : null;
    }
  },

  orders: {
    list: async () => {
      const response = await safeApi.fetch('/orders?sort=-created_at');
      return response && response.ok ? response.json() : [];
    },
    
    get: async (id) => {
      const response = await safeApi.fetch(`/orders/${id}`);
      return response && response.ok ? response.json() : null;
    }
  },

  dashboard: {
    getStats: async () => {
      const response = await safeApi.fetch('/dashboard/stats');
      return response && response.ok ? response.json() : null;
    },
    
    getAnalytics: async (period = '30d') => {
      const response = await safeApi.fetch(`/analytics?period=${period}`);
      if (!response) return null;
      if (response.error) return response; // שגיאת פיצ'ר - החזר כדי להציג הודעה
      return response.ok ? response.json() : null;
    }
  },

  coupons: {
    list: async () => {
      const response = await safeApi.fetch('/coupons');
      if (!response) return null;
      if (response.error) return response;
      return response.ok ? response.json() : [];
    },
    
    generate: async (data) => {
      const response = await safeApi.fetch('/coupons/generate', {
        method: 'POST',
        body: JSON.stringify(data)
      });
      if (!response) return null;
      if (response.error) return response;
      return response.ok ? response.json() : null;
    }
  }
};

// ============================================
// HELPER: Check if result is a feature error
// ============================================

export const isFeatureError = (result) => {
  return result && result.error && result.code === 'FEATURE_NOT_AVAILABLE';
};

// ============================================
// HELPER: Get required plan message
// ============================================

export const getUpgradeMessage = (result) => {
  if (!isFeatureError(result)) return null;
  return `This feature requires the ${result.requiredPlan || 'Pro'} plan. Upgrade to unlock!`;
};

// Export default
export default safeApi;